import { ReactNode, useMemo, useCallback, useState } from 'react';
import './FilterPanel.scss';
import { useDndContext } from '@dnd-kit/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueueContext } from '../../../Queue/QueueContext';
import { useAuthContext } from 'context/useAuthContext';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { HoneAbilityActions, HoneAbilitySubjects, QueueView } from '@hone-automation/common';
import { useDissolveGroup, useNavigateToGroup } from 'hooks/queue';
import DashboardEmail from '../../../Dashboard/DashboardEmail/DashboardEmail';
import {
  IconAssignmentNew,
  IconPromote,
  IconValidateNew,
  IconForward,
  IconNavTeam,
  IconFolder,
  IconFolderDelete,
  IconTaskNew,
  FilterAnomaly,
} from 'components/Icons';
import IconButton from '../../Common/IconButton';
import { FilterMenuItem, FilterSection } from './subcomponents';
import { useDefaultFilter } from './hooks';
import { canDropOnFilter } from './utils';
import { prefixClass } from 'lib/utils';

type FilterPanelProps = {
  /** Optional chart component to render at the top of the panel */
  chart?: ReactNode;
  /** Optional label to show next to the chart header title (e.g. default period) */
  chartLabel?: string;
};

/**
 * FilterPanel - Left sidebar for filtering requests
 * Contains: Chart (optional), To-Dos, Groups, and filter options.
 */
export const FilterPanel = ({ chart, chartLabel }: FilterPanelProps) => {
  const { state, dispatch, queryData } = useQueueContext();
  const { activeView, activeGroupId } = state;
  const { viewCounts } = queryData;
  const prefix = prefixClass('ksq2-filter-panel');
  const navigate = useNavigate();
  const location = useLocation();
  const [isChartVisible, setIsChartVisible] = useState(false);

  const { currentLocationAbility } = useAuthContext();
  const { currentLocation } = useLocationsStore(state => ({
    currentLocation: state.currentLocation,
  }));
  const { active } = useDndContext();
  const currentLocationId = currentLocation?.id || '';
  const { navigateToGroup } = useNavigateToGroup();
  const { dissolveGroup } = useDissolveGroup(currentLocationId);

  // Groups data is now derived from viewCounts in the filterCounts section below

  // Section expand state - only one section can be open at a time (accordion)
  // For now, only To-Dos exists, so it defaults to expanded
  const [expandedSection, setExpandedSection] = useState<string>('todos');

  // Use counts from context (fetched in QueueContextProvider)
  const filterCounts = viewCounts;

  // Groups from view counts (server-side), sorted alphabetically
  const groups = useMemo(() => {
    const groupSummaries = viewCounts.Groups;
    if (!Array.isArray(groupSummaries)) return [];
    return [...groupSummaries].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [viewCounts.Groups]);

  // Accountants can manage the queue, clients cannot
  const isAccountant = currentLocationAbility?.can(HoneAbilityActions.manage, HoneAbilitySubjects.Queue) ?? false;

  // Auto-select default filter based on priority: Blockers > Me > My Team > Client
  const handleSetView = useCallback(
    (view: QueueView | undefined) => {
      dispatch({ type: 'SET_ACTIVE_VIEW', payload: view });
    },
    [dispatch]
  );

  useDefaultFilter({
    filterCounts,
    activeView,
    onSetView: handleSetView,
    // dataLoaded can be passed when a loading state is available to handle empty queues
  });

  // Check if dragging and which filter items can be dropped on
  const isDragging = !!active;

  // Helper to check if a drop is allowed on a specific filter
  const checkCanDrop = useCallback(
    (targetFilter: QueueView): boolean => {
      if (!isDragging) return true;
      // Source is the current active view (where items are being dragged from)
      return canDropOnFilter(activeView, targetFilter);
    },
    [isDragging, activeView]
  );

  /** Filter items - label for 'client' changes based on user role */
  const filterItems = useMemo<
    { icon: ReactNode; view: QueueView; label: string; hideCount?: boolean; alert?: boolean }[]
  >(
    () => [
      { icon: <IconPromote />, view: 'Blockers', label: 'Blockers', alert: filterCounts.Blockers > 0 },
      { icon: <IconForward />, view: 'Me', label: 'Me' },
      { icon: <IconNavTeam />, view: 'My Team', label: 'My Team' },
      { icon: <IconAssignmentNew />, view: 'Client', label: isAccountant ? 'Client' : 'KS' },
      { icon: <IconValidateNew />, view: 'Completed', label: 'Completed' },
    ],
    [isAccountant, filterCounts.Blockers]
  );

  const handleFilterClick = (view: QueueView) => {
    // Radio behavior: clicking the same filter does nothing, must always have one selected
    if (activeView === view) return;

    // Update URL to reflect the selected filter; clear one-time deeplink params
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('requestId');
    searchParams.delete('parentId');
    searchParams.set('view', view);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });

    dispatch({ type: 'SET_ACTIVE_VIEW', payload: view });
  };

  const handleGroupClick = (groupId: string) => {
    if (activeGroupId === groupId) return;
    navigateToGroup(groupId);
  };

  const handleDissolveGroup = useCallback(
    async (groupId: string, requestCount: number) => {
      const dissolved = await dissolveGroup(groupId, 'Group', requestCount);
      if (dissolved && activeView === 'Groups' && activeGroupId) {
        dispatch({ type: 'SET_ACTIVE_GROUP_ID', payload: null });
        dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'Me' });
      }
    },
    [dissolveGroup, activeView, activeGroupId, dispatch]
  );

  const handleSectionToggle = (section: string) => {
    // Accordion behavior: toggle section, only one open at a time
    setExpandedSection(expandedSection === section ? '' : section);
  };

  const totalCount = Number(filterCounts.Me) + Number(filterCounts['My Team']) + Number(filterCounts.Client);

  return (
    <div className={prefix()}>
      {chart && (
        <div className={prefix('__chart')}>
          <div className={prefix('__chart-header')}>
            <div className={prefix('__chart-title-row')}>
              {chartLabel && <span className={prefix('__chart-label')}>{chartLabel}</span>}
            </div>
            <IconButton
              type="switch"
              rounded
              selected={isChartVisible}
              icon={<FilterAnomaly />}
              tooltip={isChartVisible ? 'hide chart' : 'show chart'}
              className={prefix('__chart-toggle')}
              onClick={() => setIsChartVisible(prev => !prev)}
            />
          </div>
          {isChartVisible && <div className={prefix('__chart-body')}>{chart}</div>}
        </div>
      )}
      <div className={prefix('__content')}>
        {/* To-Dos section */}
        <FilterSection
          title="To-Dos"
          icon={<IconTaskNew />}
          expanded={expandedSection === 'todos'}
          onToggle={() => handleSectionToggle('todos')}
          blockerCount={filterCounts.Blockers}
          count={totalCount}
        >
          {filterItems.map(({ icon, view, label, hideCount, alert }) => (
            <FilterMenuItem
              key={view}
              filterId={view}
              icon={icon}
              label={label}
              count={filterCounts[view] as number}
              selected={activeView === view}
              canDrop={checkCanDrop(view)}
              hideCount={hideCount}
              alert={alert}
              onClick={() => handleFilterClick(view)}
            />
          ))}
          {/* Groups */}
          {groups.length > 0 && (
            <FilterMenuItem variant="parent" icon={<IconFolder />} label="Groups" count={groups.length} defaultExpanded>
              {groups.map(group => (
                <FilterMenuItem
                  key={group.id}
                  variant="child"
                  filterId={group.id}
                  label={group.name}
                  labelTooltip={Number(group.requestCount || 0) === 0 ? `${group.name} (empty group)` : group.name}
                  count={Number(group.requestCount || 0)}
                  selected={activeView === 'Groups' && activeGroupId === group.id}
                  canDrop={true} // Groups always accept drops
                  onClick={() => handleGroupClick(group.id)}
                  actionIcon={<IconFolderDelete />}
                  actionTooltip="Dissolve group"
                  onAction={() => handleDissolveGroup(group.id, Number(group.requestCount || 0))}
                />
              ))}
            </FilterMenuItem>
          )}
        </FilterSection>
      </div>
      <div className={prefix('__footer')}>
        <DashboardEmail email={currentLocation?.emailId} />
      </div>
    </div>
  );
};
